import type {
  BrowserKinetic,
  BrowserKineticEvent,
  BrowserKineticIdleVehicle,
  BrowserKineticVehicleLeg,
  SimulationAssignment,
  VehiclePlayback,
  VisualizationEvent,
} from '../types/simulationPlayback';

export type TransportRequestActivityWindow = {
  startSec: number;
  endSec: number;
};

export interface PlaybackPosition {
  x: number;
  y: number;
  angle: number;
  progress: number;
  leg: BrowserKineticVehicleLeg | null;
  finished: boolean;
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** Smoothstep ease for heading blends (reduces corner snap feel). */
function smoothstep01(t: number): number {
  const u = clamp01(t);
  return u * u * (3 - 2 * u);
}

/** Short corner blend — HTML players skip heading; keep a brief ease only. */
export const DEFAULT_HEADING_BLEND_S = 0.12;

/** Shortest-path interpolate between two headings in degrees. */
export function lerpAngleDeg(fromDeg: number, toDeg: number, t: number): number {
  const delta = ((((toDeg - fromDeg) % 360) + 540) % 360) - 180;
  return fromDeg + delta * clamp01(t);
}

function normalizeAngleDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Bearing of the track segment a leg runs along, with no corner easing.
 * `smoothLegHeadingDeg` blends this toward the neighbouring legs for a smooth
 * turn, so anything that needs the true segment direction — a parked vehicle,
 * for instance — has to read this instead of the eased angle.
 */
export function legHeadingDeg(
  leg: BrowserKineticVehicleLeg,
  idleAngle: number
): number {
  const dx = leg.toX - leg.fromX;
  const dy = leg.toY - leg.fromY;
  if (dx === 0 && dy === 0) return normalizeAngleDeg(idleAngle);
  return normalizeAngleDeg((Math.atan2(dy, dx) * 180) / Math.PI);
}

function findNeighborLeg(
  legs: BrowserKineticVehicleLeg[],
  active: BrowserKineticVehicleLeg,
  direction: 'prev' | 'next'
): BrowserKineticVehicleLeg | null {
  const index = legs.indexOf(active);
  if (index < 0) {
    // Fallback when callers pass a copied leg object.
    const byTime = legs.findIndex(
      (item) => item.startS === active.startS && item.endS === active.endS
    );
    if (byTime < 0) return null;
    if (direction === 'prev') return byTime > 0 ? legs[byTime - 1] : null;
    return byTime < legs.length - 1 ? legs[byTime + 1] : null;
  }
  if (direction === 'prev') return index > 0 ? legs[index - 1] : null;
  return index < legs.length - 1 ? legs[index + 1] : null;
}

/**
 * Smooth heading near segment joints so OHTs ease into the next/previous
 * direction instead of snapping via atan2.
 */
export function smoothLegHeadingDeg(
  legs: BrowserKineticVehicleLeg[],
  leg: BrowserKineticVehicleLeg,
  simulationTime: number,
  idleAngle: number,
  headingBlendS = DEFAULT_HEADING_BLEND_S
): number {
  const durationS = Math.max(0, leg.endS - leg.startS);
  const target = legHeadingDeg(leg, idleAngle);
  if (headingBlendS <= 0 || durationS <= 0) return target;

  if (durationS < 0.1) return target;

  const timeFromStart = simulationTime - leg.startS;
  const timeToEnd = leg.endS - simulationTime;
  let angle = target;

  if (timeFromStart < headingBlendS) {
    const prev = findNeighborLeg(legs, leg, 'prev');
    if (prev) {
      const prevDur = Math.max(0, prev.endS - prev.startS);
      const turn = turnAngleDeg(prev, leg);
      if (turn >= 8 && prevDur >= 0.1) {
        const blendWindow = Math.min(headingBlendS, durationS / 2, prevDur / 2);
        if (blendWindow > 0 && timeFromStart < blendWindow) {
          const prevHeading = legHeadingDeg(prev, idleAngle);
          const u = smoothstep01(timeFromStart / blendWindow);
          angle = lerpAngleDeg(prevHeading, target, u);
        }
      }
    }
  }

  if (timeToEnd < headingBlendS) {
    const next = findNeighborLeg(legs, leg, 'next');
    if (next) {
      const nextDur = Math.max(0, next.endS - next.startS);
      const turn = turnAngleDeg(leg, next);
      if (turn >= 8 && nextDur >= 0.1) {
        const blendWindow = Math.min(headingBlendS, durationS / 2, nextDur / 2);
        if (blendWindow > 0 && timeToEnd < blendWindow) {
          const nextHeading = legHeadingDeg(next, idleAngle);
          const u = smoothstep01(1 - timeToEnd / blendWindow);
          angle = lerpAngleDeg(angle, nextHeading, u);
        }
      }
    }
  }

  return normalizeAngleDeg(angle);
}

/** Last position the vehicle occupied at a given simulation time (does not jump to home). */
export function resolveLastKnownPosition(
  legs: BrowserKineticVehicleLeg[],
  idle: BrowserKineticIdleVehicle,
  simulationTime: number
): { x: number; y: number } {
  if (legs.length === 0) return { x: idle.x, y: idle.y };

  // Legs are loaded sorted by startS; avoid per-frame copy+sort.
  if (simulationTime < legs[0]!.startS) return { x: idle.x, y: idle.y };

  let lastKnown = { x: idle.x, y: idle.y };
  for (const item of legs) {
    if (item.startS > simulationTime) break;
    if (simulationTime >= item.endS) {
      lastKnown = { x: item.toX, y: item.toY };
      continue;
    }
    lastKnown = { x: item.fromX, y: item.fromY };
  }
  return lastKnown;
}

/** Heading of the most recent completed (or current) leg — avoids yaw snap in gaps. */
export function resolveLastKnownHeadingDeg(
  legs: BrowserKineticVehicleLeg[],
  simulationTime: number,
  idleAngle: number
): number {
  if (legs.length === 0) return normalizeAngleDeg(idleAngle);
  let last: BrowserKineticVehicleLeg | null = null;
  for (const item of legs) {
    if (item.startS > simulationTime) break;
    last = item;
  }
  return last ? legHeadingDeg(last, idleAngle) : normalizeAngleDeg(idleAngle);
}

/** Travel / deadhead / home — same ranking as HTML player + findActiveApiLeg. */
function isTravelPlaybackLeg(leg: BrowserKineticVehicleLeg): boolean {
  const phase = leg.phase;
  if (!phase) {
    // Untagged motion chords beat zero-speed dwells when timelines overlap.
    return !(leg.speed === 0 || (leg.fromX === leg.toX && leg.fromY === leg.toY && !leg.segmentId));
  }
  return (
    phase === 'delivery' ||
    phase === 'deadhead' ||
    phase === 'return_home' ||
    phase === 'move'
  );
}

function playbackLegPriority(leg: BrowserKineticVehicleLeg): number {
  // Backend-authored kinetic holds own the vehicle even if a lookahead travel
  // window overlaps them. In particular, DROP's FOUP hold must not be used;
  // only the explicit OHT handover leg controls the vehicle.
  if (leg.reason === 'handover' || leg.reason === 'junctionDwell') return 0;
  return isTravelPlaybackLeg(leg) ? 1 : 2;
}

/**
 * Active geometric leg at t.
 *
 * When multi-FOUP / CA produces overlapping windows for one OHT, explicit
 * backend `handover` / `junctionDwell` holds win, then travel wins over generic
 * waits. This keeps kinetic holds authoritative without reviving the old bug
 * where an incidental process wait froze valid travel.
 *
 * Assumes legs are sorted by startS (load path sorts them). Avoids allocating
 * a filtered array on the common single-candidate path.
 */
export function findActiveVehicleLeg(
  legs: BrowserKineticVehicleLeg[],
  simulationTime: number
): BrowserKineticVehicleLeg | null {
  let best: BrowserKineticVehicleLeg | null = null;
  let overlapCount = 0;
  for (const leg of legs) {
    if (leg.startS > simulationTime) break;
    if (simulationTime >= leg.endS) continue;
    overlapCount += 1;
    if (!best) {
      best = leg;
      continue;
    }
    const aPriority = playbackLegPriority(leg);
    const bPriority = playbackLegPriority(best);
    if (aPriority !== bPriority) {
      if (aPriority < bPriority) best = leg;
      continue;
    }
    if (leg.startS !== best.startS) {
      if (leg.startS < best.startS) best = leg;
      continue;
    }
    if (leg.endS - leg.startS > best.endS - best.startS) best = leg;
  }
  if (overlapCount <= 1) return best;
  return best;
}

/** Merge playback legs and assignment timing into one active window per request. */
export function buildTransportRequestActivityWindows(
  browserKinetic: BrowserKinetic | null,
  assignments: SimulationAssignment[]
): Map<string, TransportRequestActivityWindow> {
  const windows = new Map<string, TransportRequestActivityWindow>();

  if (browserKinetic) {
    for (const legs of Object.values(browserKinetic.vehicleLegs.legsByVehicle)) {
      for (const leg of legs) {
        const existing = windows.get(leg.requestId);
        if (!existing) {
          windows.set(leg.requestId, { startSec: leg.startS, endSec: leg.endS });
          continue;
        }
        existing.startSec = Math.min(existing.startSec, leg.startS);
        existing.endSec = Math.max(existing.endSec, leg.endS);
      }
    }
  }

  for (const assignment of assignments) {
    if (windows.has(assignment.requestId)) continue;
    windows.set(assignment.requestId, {
      startSec: assignment.plannedStartSec,
      endSec: assignment.plannedStartSec + assignment.totalCostS,
    });
  }

  return windows;
}

/**
 * Ordered segment route per request, recovered from playback legs. Used when the
 * backend assignment omits `segmentIds` but the simulated legs still traveled a route.
 */
export function buildRequestSegmentPaths(
  browserKinetic: BrowserKinetic | null
): Map<string, string[]> {
  const paths = new Map<string, string[]>();
  if (!browserKinetic) return paths;

  const legsByRequest = new Map<string, BrowserKineticVehicleLeg[]>();
  for (const legs of Object.values(browserKinetic.vehicleLegs.legsByVehicle)) {
    for (const leg of legs) {
      if (!leg.segmentId) continue;
      const group = legsByRequest.get(leg.requestId) ?? [];
      group.push(leg);
      legsByRequest.set(leg.requestId, group);
    }
  }

  for (const [requestId, legs] of legsByRequest) {
    const ordered = [...legs].sort((a, b) => a.startS - b.startS || a.endS - b.endS);
    const route: string[] = [];
    for (const leg of ordered) {
      const segmentId = leg.segmentId as string;
      if (route[route.length - 1] === segmentId) continue;
      route.push(segmentId);
    }
    paths.set(requestId, route);
  }

  return paths;
}

export function isTransportRequestActiveAtTime(
  simulationTime: number,
  window: TransportRequestActivityWindow | undefined
): boolean {
  if (!window) return false;
  return simulationTime >= window.startSec && simulationTime < window.endSec;
}

/** Signed turn angle (deg) between two directed legs. */
function turnAngleDeg(a: BrowserKineticVehicleLeg, b: BrowserKineticVehicleLeg): number {
  const ax = a.toX - a.fromX;
  const ay = a.toY - a.fromY;
  const bx = b.toX - b.fromX;
  const by = b.toY - b.fromY;
  const aLen = Math.hypot(ax, ay);
  const bLen = Math.hypot(bx, by);
  if (aLen < 1e-6 || bLen < 1e-6) return 0;
  const dot = Math.max(-1, Math.min(1, (ax * bx + ay * by) / (aLen * bLen)));
  return (Math.acos(dot) * 180) / Math.PI;
}

const maxLegEndCache = new WeakMap<BrowserKineticVehicleLeg[], number>();

function maxLegEndS(legs: BrowserKineticVehicleLeg[]): number {
  const cached = maxLegEndCache.get(legs);
  if (cached != null) return cached;
  let maxEnd = 0;
  for (const item of legs) {
    if (item.endS > maxEnd) maxEnd = item.endS;
  }
  maxLegEndCache.set(legs, maxEnd);
  return maxEnd;
}

export function getPlaybackPosition(
  playback: VehiclePlayback,
  idle: BrowserKineticIdleVehicle,
  simulationTime: number,
  idleAngle = 0,
  headingBlendS = DEFAULT_HEADING_BLEND_S
): PlaybackPosition {
  const leg = findActiveVehicleLeg(playback.legs, simulationTime);
  if (!leg) {
    const hold = resolveLastKnownPosition(playback.legs, idle, simulationTime);
    return {
      x: hold.x,
      y: hold.y,
      angle: resolveLastKnownHeadingDeg(playback.legs, simulationTime, idleAngle),
      progress: 0,
      leg: null,
      finished: playback.legs.length > 0 && simulationTime >= maxLegEndS(playback.legs),
    };
  }

  const durationS = leg.endS - leg.startS;
  // HTML posAtTime: frac = (t - start) / (end - start) — pure linear on the chord.
  const progress = durationS > 0 ? clamp01((simulationTime - leg.startS) / durationS) : 1;
  const x = leg.fromX + (leg.toX - leg.fromX) * progress;
  const y = leg.fromY + (leg.toY - leg.fromY) * progress;

  return {
    x,
    y,
    // Heading only — never bend the path (HTML has no corner softener).
    angle: smoothLegHeadingDeg(
      playback.legs,
      leg,
      simulationTime,
      idleAngle,
      headingBlendS
    ),
    progress,
    leg,
    finished: false,
  };
}

export type LoadUnloadAnimation = {
  /** True while the OHT is dwelling for a FOUP pickup/drop. */
  isPaused: boolean;
  stopElapsedS?: number;
  stopDurationS?: number | null;
  /** true = pickup (load), false = drop (unload). */
  stopLoadedAction?: boolean;
  /** Load-port id from the PICKUP/DROP event driving this transfer. */
  transferPortId?: string;
  /** Event timestamp t from the API event. */
  eventTime?: number;
  /** Whether the physical 3D lifter move should execute based on vehicle state. */
  shouldExecuteTransfer?: boolean;
};

/**
 * A PICKUP / DROP the OHT hoist actually performs.
 *
 * The event action plus a present `ohtId` is the carrier contract. Backend
 * port-only events (for example `processToPortId` moves) have no `ohtId` and
 * must not drive an OHT lifter or crane handoff.
 */
export function isOhtCarrierTransferEvent(
  event: BrowserKineticEvent
): event is BrowserKineticEvent & { ohtId: string } {
  if (event.type !== 'PICKUP' && event.type !== 'DROP') return false;
  return typeof event.ohtId === 'string' && event.ohtId.length > 0;
}

/** Empty-segment / zero-speed dwell at a port (pickup, drop, or process wait). */
export function isStationaryLeg(leg: BrowserKineticVehicleLeg): boolean {
  if (leg.speed === 0) return true;
  if (leg.segmentId) return false;
  return leg.fromX === leg.toX && leg.fromY === leg.toY;
}

/**
 * Visual transfer fractions — must match OhtVehicle3DModel / Stocker3DModel:
 * DROP:  0–0.35 descend, 0.35–0.65 release on tray, 0.65–1.0 stocker stores to rack
 * PICKUP: 0–0.45 retrieve to tray, 0.45–0.75 OHT descends, 0.75–1.0 OHT ascends loaded
 */
export const TRANSFER_VISUAL_FRACS = {
  dropOhtHandoff: 0.5,
  /** DROP inventory / rack FOUP appear only after store-into-slot finishes. */
  dropStockerCommit: 1.0,
  /** PICKUP inventory / stocker FOUP count decrements when stocker places FOUP on its tray (at progress 0.45). */
  pickupStockerCommit: 0.45,
  pickupOhtHandoff: 0.75,
} as const;

const TRANSFER_EVENT_LOOKBACK_S = 1.5;
const TRANSFER_EVENT_LOOKAHEAD_S = 3.5;

/**
 * Stationary dwell that owns a PICKUP/DROP for visual sequencing.
 * Events often stamp before the OHT finishes travel — look ahead for arrival.
 */
/**
 * Stationary-leg pool per legs array. `findTransferDwellForEvent` used to filter
 * the vehicle's full leg list on every call (once per event, per animated object,
 * per frame); the filtered pool only depends on the legs array, so memoise it.
 */
const stationaryPoolCache = new WeakMap<
  BrowserKineticVehicleLeg[],
  BrowserKineticVehicleLeg[]
>();

/** Resolved dwell per (legs array → event), so repeat frames reuse the match. */
const transferDwellCache = new WeakMap<
  BrowserKineticVehicleLeg[],
  Map<BrowserKineticEvent, BrowserKineticVehicleLeg | null>
>();

export function findTransferDwellForEvent(
  event: BrowserKineticEvent,
  legs: BrowserKineticVehicleLeg[] | undefined
): BrowserKineticVehicleLeg | null {
  if (!legs?.length || !event.ohtId) return null;
  if (!isOhtCarrierTransferEvent(event)) return null;

  let perEvent = transferDwellCache.get(legs);
  if (perEvent?.has(event)) return perEvent.get(event) ?? null;
  if (!perEvent) {
    perEvent = new Map();
    transferDwellCache.set(legs, perEvent);
  }

  const resolved = computeTransferDwellForEvent(event, legs);
  perEvent.set(event, resolved);
  return resolved;
}

function computeTransferDwellForEvent(
  event: BrowserKineticEvent,
  legs: BrowserKineticVehicleLeg[]
): BrowserKineticVehicleLeg | null {
  let pool = stationaryPoolCache.get(legs);
  if (!pool) {
    pool = legs.filter(
      (leg) =>
        (leg.vehicleId === event.ohtId || !leg.vehicleId) &&
        isStationaryLeg(leg) &&
        leg.endS > leg.startS
    );
    stationaryPoolCache.set(legs, pool);
  }
  if (pool.length === 0) return null;

  const portId = typeof event.portId === 'string' ? event.portId : '';
  const portMatch = (leg: BrowserKineticVehicleLeg) =>
    !!portId && (leg.fromId === portId || leg.toId === portId);
  // `process_wait` holds are stitched in to cover clearance gaps between segment
  // windows. They look like dwells, so a transfer stamped mid-gap used to latch
  // onto one and animate the hoist away from the actual load port.
  const isHold = (leg: BrowserKineticVehicleLeg) => leg.phase === 'process_wait';

  const contains = (leg: BrowserKineticVehicleLeg) =>
    event.t >= leg.startS && event.t <= leg.endS;
  // A known load-port id must stay on that port. Binding a stocker PICKUP to
  // the next tool dwell hid the FOUP until the OHT arrived there.
  const containing = portId
    ? pool.find((leg) => contains(leg) && portMatch(leg) && !isHold(leg)) ??
      pool.find((leg) => contains(leg) && portMatch(leg)) ??
      null
    : pool.find((leg) => contains(leg) && !isHold(leg)) ?? pool.find(contains) ?? null;
  if (containing) return containing;

  let best: BrowserKineticVehicleLeg | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const leg of pool) {
    if (portId && !portMatch(leg)) continue;
    const deltaStart = leg.startS - event.t;
    if (
      deltaStart < -TRANSFER_EVENT_LOOKBACK_S ||
      deltaStart > TRANSFER_EVENT_LOOKAHEAD_S
    ) {
      continue;
    }
    const score = Math.abs(deltaStart) + (isHold(leg) ? 1e3 : 0);
    if (score < bestScore) {
      best = leg;
      bestScore = score;
    }
  }
  return best;
}

/** World-space radius: hoist / dock snap only when the OHT is actually there. */
export const OHT_TRANSFER_DOCK_RADIUS = 48;

export function isOhtNearTransferDock(
  oht: { x: number; y: number },
  dock: { x: number; y: number },
  radius = OHT_TRANSFER_DOCK_RADIUS
): boolean {
  const dx = oht.x - dock.x;
  const dy = oht.y - dock.y;
  return dx * dx + dy * dy <= radius * radius;
}

/**
 * Pin the vehicle onto a station dock only when that dock already sits on the
 * rail next to the OHT. Tool equipment stays on the nearby track node — the
 * hoist works from there instead of flying the vehicle onto the tool body.
 */
export function shouldPinOhtToTransferDock(
  ownerType: string | undefined,
  oht: { x: number; y: number },
  dock: { x: number; y: number } | null | undefined
): dock is { x: number; y: number } {
  if (ownerType !== 'stocker' || !dock) return false;
  return isOhtNearTransferDock(oht, dock);
}

/** Tool transfers hoist at the nearby node; stocker hoist still needs the tray. */
export function shouldPlayOhtHoistAtDock(
  ownerType: string | undefined,
  oht: { x: number; y: number },
  dock: { x: number; y: number } | null | undefined
): boolean {
  if (!dock || ownerType !== 'stocker') return true;
  return isOhtNearTransferDock(oht, dock);
}

/**
 * Stationary leg holding the OHT's real stop position for a transfer.
 *
 * {@link findTransferDwellForEvent} deliberately refuses to match a leg whose
 * id is not the load port, because binding a stocker transfer to a neighbouring
 * dwell moves the hoist to the wrong tray. Tools broke that assumption: they
 * park on a nearby track node, so the leg carries the node id and a port-keyed
 * filter finds nothing. Without a leg there is no handoff coordinate, and the
 * kit strands the FOUP on its own tray while the OHT hoists out at the rail.
 *
 * Widen the search to the transport request only when the port itself appears
 * on no leg, which keeps the stocker guarantee intact.
 */
export function findTransferStopLeg(
  event: { t: number; portId?: string; requestId?: string },
  legs: readonly BrowserKineticVehicleLeg[] | undefined
): BrowserKineticVehicleLeg | null {
  if (!legs?.length) return null;
  const stationary = legs.filter(isStationaryLeg);
  if (stationary.length === 0) return null;

  const portId = typeof event.portId === 'string' ? event.portId : '';
  const byPort = portId
    ? stationary.filter((leg) => leg.fromId === portId || leg.toId === portId)
    : [];
  const scoped =
    byPort.length > 0
      ? byPort
      : event.requestId
        ? stationary.filter((leg) => leg.requestId === event.requestId)
        : [];
  if (scoped.length === 0) return null;

  // Score by distance to the stop interval rather than to its start. Backend
  // events often stamp just before arrival, so a stop that has not begun yet is
  // still the right one — while an earlier stop on the same request (the pickup
  // dwell of the very same move) must not win just because it contains the
  // stamp. Anchoring to the wrong stop starts the hoist mid-travel.
  const gapS = (leg: BrowserKineticVehicleLeg) => {
    if (event.t < leg.startS) return leg.startS - event.t;
    if (event.t > leg.endS) return event.t - leg.endS;
    return 0;
  };
  const maxGapS = Math.max(TRANSFER_EVENT_LOOKBACK_S, TRANSFER_EVENT_LOOKAHEAD_S);

  let best: BrowserKineticVehicleLeg | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const leg of scoped) {
    const gap = gapS(leg);
    if (gap > maxGapS) continue;
    if (gap < bestScore) {
      best = leg;
      bestScore = gap;
    }
  }
  return best;
}

/**
 * Whether the vehicle is parked at `t` on its own clock.
 *
 * Pose and replay snapshot do not share a timeline: junction waits rewind a
 * single vehicle via {@link playbackDelayStore}, while the snapshot stays on
 * the global clock. The hoist is driven by the snapshot, so without this gate a
 * delayed vehicle lowers its legs while it is still travelling.
 *
 * Deliberately not {@link isStationaryLeg}: that treats any segment-tagged leg
 * as travel so it can pick dwells out of a leg list, which would classify an
 * OHT parked on a tagged segment as moving and suppress the whole transfer.
 * Only actual displacement counts as travel here. Absence of a covering leg
 * means idle/parked, so the hoist is allowed.
 */
export function isVehicleStoppedAt(
  legs: readonly BrowserKineticVehicleLeg[] | undefined,
  t: number
): boolean {
  if (!legs?.length) return true;
  const active = legs.find((leg) => t >= leg.startS && t <= leg.endS);
  if (!active) return true;
  return active.fromX === active.toX && active.fromY === active.toY;
}

/**
 * Shortest transfer the animation is allowed to occupy. When an event is
 * stamped at (or past) the end of its dwell there is no room left to play the
 * handoff, so a capped window ending at the dwell is used rather than flashing
 * it in one frame.
 */
const MIN_TRANSFER_WINDOW_S = 0.5;

/**
 * Portion of the transfer window the hoist spends lowering on a DROP. Pinned to
 * the release beat in `OhtVehicle3DModel` / `EQUIPMENT_TRANSFER_FRACS`, so the
 * legs are always fully down at the moment the FOUP changes hands.
 */
const HOIST_DESCENT_FRACTION = 0.35;

/**
 * Wall-clock seconds the legs take to cover the full hoist stroke. This is the
 * knob for leg speed: raise it for a slower, heavier-looking hoist.
 */
export const HOIST_TRAVEL_SEC = 6.3;

/**
 * Longest wall-clock span the hoist / shutter / FOUP sequence may occupy.
 * Backend dwells are often tens of seconds of parking at a port; stretching the
 * lifter across that whole window makes OHT legs crawl. Cap the visual handoff
 * and leave the leftover dwell as idle wait with the hoist retracted.
 *
 * Derived from the hoist rate rather than chosen directly, because the leg
 * travel is the visible constraint — every other beat rides on the same window.
 * Shorter dwells still shrink the window, which speeds the legs back up.
 */
export const MAX_TRANSFER_ANIMATION_S =
  HOIST_TRAVEL_SEC / HOIST_DESCENT_FRACTION;

export type TransferWindow = { startS: number; durationS: number };

/** Native replay counterpart to the legacy compiler's arrival/settle phase. */
export function alignEquipmentTransfersToStops(
  events: readonly VisualizationEvent[],
  browser: Pick<BrowserKinetic, 'vehicleLegs' | 'toolPortOwners'>
): VisualizationEvent[] {
  const adjusted = events.map((event) => ({ ...event }));
  const groups = new Map<string, VisualizationEvent[]>();
  for (const event of adjusted) {
    const list = groups.get(event.operationId) ?? [];
    list.push(event);
    groups.set(event.operationId, list);
  }

  for (const operation of groups.values()) {
    const physical = operation.filter((event) =>
      event.kind === 'EQUIPMENT_TRANSFER' ||
      event.kind === 'OHT_PICKUP' ||
      event.kind === 'OHT_DROP' ||
      event.kind === 'INVENTORY_COMMIT'
    );
    const carrier = physical.find(
      (event) =>
        event.ohtId &&
        event.portId &&
        (event.action === 'PICKUP' || event.action === 'DROP') &&
        !!browser.toolPortOwners?.[event.portId]
    );
    if (!carrier?.ohtId || !carrier.portId || physical.length === 0) continue;

    const dwell = findTransferStopLeg(carrier, browser.vehicleLegs.legsByVehicle[carrier.ohtId]);
    if (!dwell) continue;

    const originalStart = Math.min(...physical.map((event) => event.t));
    const originalEnd = Math.max(
      ...physical.map((event) => event.endT ?? event.t)
    );
    const settleS = Math.min(
      1,
      Math.max(0, dwell.endS - dwell.startS - MIN_TRANSFER_WINDOW_S)
    );
    const targetStart = Math.max(originalStart, dwell.startS + settleS);
    const originalDuration = Math.max(0, originalEnd - originalStart);
    const availableDuration = Math.max(0, dwell.endS - targetStart);
    const scale =
      originalDuration > 0
        ? Math.min(1, availableDuration / originalDuration)
        : 1;

    for (const event of physical) {
      event.t = targetStart + (event.t - originalStart) * scale;
      if (event.endT !== undefined) {
        event.endT =
          targetStart + (event.endT - originalStart) * scale;
      }
      event.handoffX = dwell.toX;
      event.handoffY = dwell.toY;
      event.preSettled = true;
    }
  }

  return adjusted
    .sort((a, b) => a.t - b.t || a.sequence - b.sequence)
    .map((event, sequence) => ({ ...event, sequence }));
}

/**
 * When the hoist / crane sequence actually runs inside a dwell.
 *
 * The dwell covers arrival → departure, and the OHT often parks well before the
 * backend stamps the PICKUP / DROP. Keying progress to `dwell.startS` therefore
 * started the crane, the shutter and the FOUP motion *before* the event that
 * authorises them. The transfer owns the dwell from the event onward instead —
 * but never longer than {@link MAX_TRANSFER_ANIMATION_S}.
 */
export function resolveTransferWindow(
  event: BrowserKineticEvent,
  dwell: Pick<BrowserKineticVehicleLeg, 'startS' | 'endS'>
): TransferWindow {
  // Live streams provide the transfer visual's authoritative simulation span.
  // Do not stretch or cap it to the vehicle dwell: on DROP the FOUP-side hold
  // may continue after the OHT's handover leg has ended.
  if (typeof event.holdSimSec === 'number' && event.holdSimSec > 0) {
    return { startS: event.t, durationS: event.holdSimSec };
  }

  const dwellDurationS = Math.max(0, dwell.endS - dwell.startS);
  const startS = Math.min(Math.max(event.t, dwell.startS), dwell.endS);
  const remainingS = dwell.endS - startS;
  if (remainingS < Math.min(MIN_TRANSFER_WINDOW_S, dwellDurationS)) {
    // Not enough room after the stamp — play a capped window that ends with the
    // dwell, so a late stamp on a long park does not stretch the hoist for a
    // minute.
    const durationS = Math.min(dwellDurationS, MAX_TRANSFER_ANIMATION_S);
    return {
      startS: Math.max(dwell.startS, dwell.endS - durationS),
      durationS,
    };
  }
  return {
    startS,
    durationS: Math.min(remainingS, MAX_TRANSFER_ANIMATION_S),
  };
}

function eventVisualTimes(
  event: BrowserKineticEvent,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): { ohtHandoffT: number; stockerCommitT: number } {
  const legs = event.ohtId ? legsByVehicle?.[event.ohtId] : undefined;
  const dwell = findTransferDwellForEvent(event, legs);
  if (!dwell) {
    return { ohtHandoffT: event.t, stockerCommitT: event.t };
  }
  // Same window the visuals run on, so FOUP ownership flips mid-animation
  // instead of drifting away from what the crane is doing.
  const { startS, durationS } = resolveTransferWindow(event, dwell);
  if (event.type === 'DROP') {
    return {
      ohtHandoffT: startS + durationS * TRANSFER_VISUAL_FRACS.dropOhtHandoff,
      stockerCommitT: startS + durationS * TRANSFER_VISUAL_FRACS.dropStockerCommit,
    };
  }
  return {
    ohtHandoffT: startS + durationS * TRANSFER_VISUAL_FRACS.pickupOhtHandoff,
    stockerCommitT: startS + durationS * TRANSFER_VISUAL_FRACS.pickupStockerCommit,
  };
}



export type EventStateAnalysis = {
  shouldExecuteTransfer: Map<BrowserKineticEvent, boolean>;
  isLoadedAfterEvent: Map<BrowserKineticEvent, boolean>;
  isLoadedBeforeEvent: Map<BrowserKineticEvent, boolean>;
};

/**
 * Per-frame every animated object (tools, stockers, OHTs) resolves transfer
 * state, and each resolve used to rebuild this analysis from scratch. The result
 * only depends on the (immutable during a run) events array, so cache it keyed
 * by that array — same pattern as `maxLegEndCache`.
 */
const eventAnalysisCache = new WeakMap<BrowserKineticEvent[], EventStateAnalysis>();

/** Event → array index, so hot paths stop calling `events.indexOf` (O(n) each). */
const eventIndexCache = new WeakMap<
  BrowserKineticEvent[],
  Map<BrowserKineticEvent, number>
>();

function eventIndexMap(
  events: BrowserKineticEvent[]
): Map<BrowserKineticEvent, number> {
  const cached = eventIndexCache.get(events);
  if (cached) return cached;
  const map = new Map<BrowserKineticEvent, number>();
  events.forEach((event, index) => map.set(event, index));
  eventIndexCache.set(events, map);
  return map;
}

export function analyzeEventSequence(
  events: BrowserKineticEvent[]
): EventStateAnalysis {
  const cached = eventAnalysisCache.get(events);
  if (cached) return cached;
  const analysis = computeEventSequence(events);
  eventAnalysisCache.set(events, analysis);
  return analysis;
}

function computeEventSequence(
  events: BrowserKineticEvent[]
): EventStateAnalysis {
  const shouldExecuteTransfer = new Map<BrowserKineticEvent, boolean>();
  const isLoadedAfterEvent = new Map<BrowserKineticEvent, boolean>();
  const isLoadedBeforeEvent = new Map<BrowserKineticEvent, boolean>();

  if (!events || events.length === 0) {
    return { shouldExecuteTransfer, isLoadedAfterEvent, isLoadedBeforeEvent };
  }

  const eventsByVehicle = new Map<string, BrowserKineticEvent[]>();
  for (const event of events) {
    if (!event.ohtId) continue;
    let list = eventsByVehicle.get(event.ohtId);
    if (!list) {
      list = [];
      eventsByVehicle.set(event.ohtId, list);
    }
    list.push(event);
  }

  for (const [, vehicleEvents] of eventsByVehicle) {
    const transferEvents = vehicleEvents.filter(isOhtCarrierTransferEvent);
    if (transferEvents.length === 0) continue;

    let isLoaded = transferEvents[0].type === 'DROP';

    for (const event of transferEvents) {
      isLoadedBeforeEvent.set(event, isLoaded);

      if (event.type === 'DROP') {
        if (isLoaded) {
          shouldExecuteTransfer.set(event, true);
          isLoaded = false;
        } else {
          shouldExecuteTransfer.set(event, false);
          isLoaded = false;
        }
      } else if (event.type === 'PICKUP') {
        if (!isLoaded) {
          shouldExecuteTransfer.set(event, true);
          isLoaded = true;
        } else {
          shouldExecuteTransfer.set(event, false);
          isLoaded = true;
        }
      }

      isLoadedAfterEvent.set(event, isLoaded);
    }
  }

  return { shouldExecuteTransfer, isLoadedAfterEvent, isLoadedBeforeEvent };
}

/**
 * Process and log simulation events strictly in API array index sequence as simulation time progresses.
 */
export function processSimulationEventLogs(
  events: BrowserKineticEvent[],
  simulationTime: number,
  lastIndexRef: { current: number },
  _stockerPortOwners: Record<string, string> = {},
  _legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): void {
  void _stockerPortOwners;
  void _legsByVehicle;
  if (!events || events.length === 0) return;

  // Reset index if time rewound
  if (
    lastIndexRef.current >= events.length ||
    (lastIndexRef.current >= 0 && events[lastIndexRef.current].t > simulationTime + 0.5)
  ) {
    lastIndexRef.current = -1;
  }

  const analysis = analyzeEventSequence(events);

  let nextIdx = lastIndexRef.current + 1;
  while (nextIdx < events.length) {
    const event = events[nextIdx];
    if (event.t > simulationTime) break;

    if (event.type === 'PICKUP' || event.type === 'DROP') {
      const ohtId = event.ohtId || 'unknown';
      const isPickup = event.type === 'PICKUP';
      const eventType = isPickup ? 'PICKUP' : 'DROP';
      const timeStr = ` | t: ${event.t}`;

      if (!isOhtCarrierTransferEvent(event)) {
        console.log(
          `[OHT Event] ${eventType} internal relocation${timeStr} | OHT ID: ${ohtId} | ` +
            `${event.fromPortId} → ${event.toPortId} (OHT hoist not involved)`
        );
      } else {
        const portId = typeof event.portId === 'string' ? event.portId : 'unknown';
        const ohtStatus = isPickup ? 'LOAD' : 'UNLOAD';
        const shouldExecute = analysis.shouldExecuteTransfer.get(event) ?? true;
        const isLoadedAfter = analysis.isLoadedAfterEvent.get(event) ?? isPickup;
        const statusDetail = !shouldExecute ? ' (skipped transfer)' : '';

        console.log(
          `[OHT Event] ${eventType} event${statusDetail}${timeStr} | OHT Status: ${ohtStatus} | OHT ID: ${ohtId} | Port: ${portId} | Carried FOUP: ${isLoadedAfter ? 'YES' : 'NO'}`
        );
      }
    }

    lastIndexRef.current = nextIdx;
    nextIdx += 1;
  }
}

type TimedTransferEvent = { event: BrowserKineticEvent; visualT: number };

/**
 * Sorted per-vehicle transfer timeline. Rebuilding + sorting this on every
 * `isLoadedAtTime` call (per vehicle, per frame) was a top allocator; the result
 * only depends on the events array and the legs map, both stable during a run.
 */
const loadTimelineCache = new WeakMap<
  BrowserKineticEvent[],
  Map<string, TimedTransferEvent[]>
>();

function loadTimelineForVehicle(
  events: BrowserKineticEvent[],
  ohtId: string,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): TimedTransferEvent[] {
  // `visualT` differs depending on whether legs are supplied (hoist-handoff time
  // vs raw event.t), and the same events array is queried both ways, so the key
  // must distinguish them. A run keeps a single legs map, so a boolean suffices.
  let perVehicle = loadTimelineCache.get(events);
  if (!perVehicle) {
    perVehicle = new Map();
    loadTimelineCache.set(events, perVehicle);
  }
  const key = legsByVehicle ? `${ohtId}|legs` : ohtId;
  const cached = perVehicle.get(key);
  if (cached) return cached;

  const indexOfEvent = eventIndexMap(events);
  const timed: TimedTransferEvent[] = [];
  for (const event of events) {
    if (event.ohtId !== ohtId || !isOhtCarrierTransferEvent(event)) continue;
    const visualT = legsByVehicle
      ? eventVisualTimes(event, legsByVehicle).ohtHandoffT
      : event.t;
    timed.push({ event, visualT });
  }
  timed.sort(
    (a, b) =>
      a.visualT - b.visualT ||
      (indexOfEvent.get(a.event) ?? 0) - (indexOfEvent.get(b.event) ?? 0)
  );
  perVehicle.set(key, timed);
  return timed;
}

/**
 * Whether an OHT is carrying a FOUP at `simulationTime`.
 * With vehicle legs, PICKUP/DROP flip at the visual hoist handoff (not raw event.t).
 */
export function isLoadedAtTime(
  events: BrowserKineticEvent[],
  ohtId: string,
  simulationTime: number,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): boolean {
  const analysis = analyzeEventSequence(events);
  const timed = loadTimelineForVehicle(events, ohtId, legsByVehicle);

  let latestApplied: BrowserKineticEvent | null = null;
  let nextPending: BrowserKineticEvent | null = null;
  for (const item of timed) {
    if (item.visualT <= simulationTime) latestApplied = item.event;
    else if (!nextPending) nextPending = item.event;
  }

  if (latestApplied) {
    const loadedState = analysis.isLoadedAfterEvent.get(latestApplied);
    if (loadedState !== undefined) return loadedState;
    return latestApplied.type === 'PICKUP';
  }
  if (nextPending) {
    const loadedState = analysis.isLoadedBeforeEvent.get(nextPending);
    if (loadedState !== undefined) return loadedState;
    return nextPending.type === 'DROP';
  }
  return false;
}

/**
 * Resolve 3D lifter pickup/drop animation from a stationary vehicle leg.
 *
 * Empty `segmentIds` legs become zero-speed dwells. A PICKUP/DROP event inside
 * the window sets the action; otherwise non-`process_wait` dwells infer
 * pickup vs drop from whether the OHT was already carrying a FOUP.
 */
/**
 * Time-independent part of {@link resolveLoadUnloadAnimation}: which event owns
 * this dwell, and the derived flags. Only `stopElapsedS` depends on the clock,
 * so everything here is computed once per leg and reused across frames. This is
 * the hot path — every tool / stocker / OHT resolves it for every vehicle each
 * frame — so the per-leg cache turns a full event scan into a Map lookup.
 */
type LoadUnloadPlan = Omit<LoadUnloadAnimation, 'stopElapsedS'> & {
  /** Clock time progress is measured from (event-anchored, not dwell start). */
  windowStartS?: number;
};

// Keyed by events → leg: the plan depends on both, and a leg object can be
// resolved against different event sets (e.g. across runs or in tests).
const loadUnloadPlanCache = new WeakMap<
  BrowserKineticEvent[],
  WeakMap<BrowserKineticVehicleLeg, LoadUnloadPlan>
>();

function computeLoadUnloadPlan(
  leg: BrowserKineticVehicleLeg,
  events: BrowserKineticEvent[],
  ohtId: string,
  durationS: number,
  vehicleLegs?: BrowserKineticVehicleLeg[]
): LoadUnloadPlan {
  const pool = vehicleLegs?.length ? vehicleLegs : [leg];
  let transferEvent: BrowserKineticEvent | null = null;
  const indexOfEvent = eventIndexMap(events);

  // Each transfer event maps uniquely to its single best owner dwell leg.
  for (const event of events) {
    if (event.ohtId !== ohtId || !isOhtCarrierTransferEvent(event)) continue;
    const ownerDwell = findTransferDwellForEvent(event, pool);
    if (ownerDwell && ownerDwell.startS === leg.startS && ownerDwell.endS === leg.endS) {
      if (
        !transferEvent ||
        (indexOfEvent.get(event) ?? 0) >= (indexOfEvent.get(transferEvent) ?? 0)
      ) {
        transferEvent = event;
      }
    }
  }

  if (transferEvent) {
    const analysis = analyzeEventSequence(events);
    const shouldExecute = analysis.shouldExecuteTransfer.get(transferEvent) ?? true;
    const stopLoadedAction = transferEvent.type === 'PICKUP';
    const transferPortId =
      typeof transferEvent.portId === 'string' && transferEvent.portId.length > 0
        ? transferEvent.portId
        : undefined;
    const window = resolveTransferWindow(transferEvent, leg);

    return {
      isPaused: shouldExecute,
      stopDurationS: window.durationS,
      windowStartS: window.startS,
      stopLoadedAction,
      transferPortId,
      eventTime: transferEvent.t,
      shouldExecuteTransfer: shouldExecute,
    };
  }

  if (leg.phase === 'process_wait') {
    return { isPaused: false };
  }

  const isLoaded = isLoadedAtTime(events, ohtId, leg.startS - Number.EPSILON);
  let hasExplicitPickups = false;
  let hasExplicitDrops = false;
  for (const e of events) {
    if (e.ohtId !== ohtId || !isOhtCarrierTransferEvent(e)) continue;
    if (e.type === 'PICKUP') hasExplicitPickups = true;
    else hasExplicitDrops = true;
  }

  if (!isLoaded && hasExplicitPickups) {
    // Empty vehicle on a dwell without a matching PICKUP event -> DO NOT infer extra pickup!
    return { isPaused: false };
  }
  if (isLoaded && hasExplicitDrops) {
    // Loaded vehicle on a dwell without a matching DROP event -> DO NOT infer extra drop!
    return { isPaused: false };
  }

  // Infer action when explicit events for this action are absent in log:
  return {
    isPaused: true,
    stopDurationS: durationS,
    stopLoadedAction: !isLoaded,
  };
}

export function resolveLoadUnloadAnimation(
  leg: BrowserKineticVehicleLeg | null,
  events: BrowserKineticEvent[],
  ohtId: string,
  simulationTime: number,
  vehicleLegs?: BrowserKineticVehicleLeg[]
): LoadUnloadAnimation {
  if (!leg || !isStationaryLeg(leg)) return { isPaused: false };

  const durationS = Math.max(0, leg.endS - leg.startS);
  if (durationS <= 0) return { isPaused: false };

  let planByLeg = loadUnloadPlanCache.get(events);
  if (!planByLeg) {
    planByLeg = new WeakMap();
    loadUnloadPlanCache.set(events, planByLeg);
  }
  let plan = planByLeg.get(leg);
  if (!plan) {
    plan = computeLoadUnloadPlan(leg, events, ohtId, durationS, vehicleLegs);
    planByLeg.set(leg, plan);
  }

  const { windowStartS, ...animation } = plan;
  // A non-paused plan carries no dwell fields — hand it back untouched.
  const windowDurationS = animation.stopDurationS;
  if (windowDurationS === undefined || windowDurationS === null) return animation;

  return {
    ...animation,
    stopElapsedS: Math.max(
      0,
      Math.min(windowDurationS, simulationTime - (windowStartS ?? leg.startS))
    ),
  };
}

/** Map load-port ids → owning stocker element ids from the layout. */
export function buildStockerPortOwnerMap(
  elements: Array<{ id: string; type: string; customProperties?: Record<string, unknown> }>
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const el of elements) {
    if (el.type !== 'stocker') continue;
    const ports = el.customProperties?.ports;
    if (!Array.isArray(ports)) continue;
    for (const port of ports) {
      if (!port || typeof port !== 'object') continue;
      const id = (port as { id?: unknown }).id;
      if (typeof id === 'string' && id.length > 0) map[id] = el.id;
    }
  }
  return map;
}

/** Map load-port ids → owning tool/equipment instance ids. */
export function buildToolPortOwnerMap(
  instances: Array<{
    id: string;
    objectType?: string;
    overrides?: { ports?: Array<{ id?: string }> };
  }>
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const instance of instances) {
    if (instance.objectType && instance.objectType !== 'equipment') continue;
    const ports = instance.overrides?.ports;
    if (!Array.isArray(ports)) continue;
    for (const port of ports) {
      if (!port || typeof port.id !== 'string' || !port.id) continue;
      map[port.id] = instance.id;
    }
  }
  return map;
}

function readEventInventory(event: BrowserKineticEvent, after: boolean): number | null {
  if (after) {
    if (typeof event.inventoryAfter === 'number') return event.inventoryAfter;
    if (typeof event.availableInventoryAfter === 'number') return event.availableInventoryAfter;
  } else {
    if (typeof event.inventoryBefore === 'number') return event.inventoryBefore;
    if (typeof event.availableInventoryBefore === 'number') {
      return event.availableInventoryBefore;
    }
  }
  return null;
}

/**
 * Replay PICKUP/DROP deltas for a set of ports.
 * DROP commits only after the store-into-slot phase (OHT → tray → rack).
 * PICKUP commits when retrieve starts (FOUP leaves the rack).
 */
function replayStockerInventory(
  portIds: ReadonlySet<string>,
  events: BrowserKineticEvent[],
  simulationTime: number,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): number {
  const relevant = events.filter(
    (event) =>
      (event.type === 'PICKUP' || event.type === 'DROP') &&
      typeof event.portId === 'string' &&
      portIds.has(event.portId)
  );
  if (relevant.length === 0) return 0;

  let earliest = relevant[0]!;
  for (const event of relevant) {
    if (event.t < earliest.t) earliest = event;
  }
  let count = Math.max(0, readEventInventory(earliest, false) ?? 0);

  const applied = relevant
    .map((event) => ({
      event,
      commitT: legsByVehicle
        ? eventVisualTimes(event, legsByVehicle).stockerCommitT
        : event.t,
    }))
    .filter((item) => item.commitT <= simulationTime)
    .sort((a, b) => a.commitT - b.commitT || a.event.t - b.event.t);

  for (const { event } of applied) {
    if (typeof event.delta === 'number') {
      count = Math.max(0, count + event.delta);
    } else if (event.type === 'DROP') {
      count += 1;
    } else {
      count = Math.max(0, count - 1);
    }
  }
  return count;
}

/**
 * FOUP count on a single load port at `simulationTime`, from PICKUP/DROP events.
 * When legs are provided, DROP waits until the store-into-slot phase finishes.
 */
export function getPortInventoryAtTime(
  events: BrowserKineticEvent[],
  portId: string,
  simulationTime: number,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): number {
  return replayStockerInventory(
    new Set([portId]),
    events,
    simulationTime,
    legsByVehicle
  );
}

/**
 * Live FOUP counts keyed by owner id (stocker or tool).
 * Uses delta replay per owner so multi-port absolute inventoryAfter values
 * are not double-counted. Optional legs defer DROP commits for stockers.
 */
export function getOwnerFoupCountsAtTime(
  events: BrowserKineticEvent[],
  portToOwnerId: Record<string, string>,
  simulationTime: number,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): Record<string, number> {
  const portsByOwner = new Map<string, Set<string>>();
  for (const [portId, ownerId] of Object.entries(portToOwnerId)) {
    if (!ownerId) continue;
    let set = portsByOwner.get(ownerId);
    if (!set) {
      set = new Set();
      portsByOwner.set(ownerId, set);
    }
    set.add(portId);
  }

  const byOwner: Record<string, number> = {};
  for (const [ownerId, portIds] of portsByOwner) {
    byOwner[ownerId] = replayStockerInventory(
      portIds,
      events,
      simulationTime,
      legsByVehicle
    );
  }
  return byOwner;
}

/**
 * Live stocker FOUP counts keyed by layout stocker id.
 * DROP adds only after tray→rack store completes when vehicle legs are provided.
 */
export function getStockerFoupCountsAtTime(
  events: BrowserKineticEvent[],
  portToStockerId: Record<string, string>,
  simulationTime: number,
  legsByVehicle?: Record<string, BrowserKineticVehicleLeg[]>
): Record<string, number> {
  return getOwnerFoupCountsAtTime(
    events,
    portToStockerId,
    simulationTime,
    legsByVehicle
  );
}

/** Live tool FOUP counts keyed by equipment instance id (LOAD + UNLOAD summed). */
export function getToolFoupCountsAtTime(
  events: BrowserKineticEvent[],
  portToToolId: Record<string, string>,
  simulationTime: number
): Record<string, number> {
  return getOwnerFoupCountsAtTime(events, portToToolId, simulationTime);
}
